import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { canAccessRoute } from '../helpers/roleUtils';

/**
 * ProtectedRoute component that checks if user has permission to access a route
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {Array} props.allowedRoles - Array of roles that can access this route
 * @param {string} props.fallbackPath - Path to redirect to if access is denied
 * @returns {React.ReactNode} Protected route or redirect
 */
const ProtectedRoute = ({ 
  children, 
  allowedRoles = [], 
  fallbackPath = '/admin/dashboard/moderator' 
}) => {
  const { user, otherUser, isAuthenticated } = useSelector((state) => state.auth);
  const location = useLocation();

  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  const isGroupAdminContextPath =
    location.pathname === '/admin/station-groups' || location.pathname.startsWith('/admin/group-admin');

  // Check if user can access the current route (use otherUser for group-admin context while acting as station)
  const effectiveUser = (isGroupAdminContextPath && otherUser && (otherUser.attributes?.roles || []).includes('group_admin'))
    ? otherUser
    : user;
  const canAccess = canAccessRoute(effectiveUser, location.pathname);

  // If user cannot access the route, redirect to fallback
  if (!canAccess) {
    return <Navigate to={fallbackPath} replace />;
  }

  // If specific roles are required, check if user has any of them (or otherUser for station-groups)
  if (allowedRoles.length > 0) {
    const userRoles = user.attributes?.roles || [];
    const hasRequiredRole = allowedRoles.some(role => userRoles.includes(role));
    const isStationGroupsPath = isGroupAdminContextPath;
    const otherUserIsGroupAdmin = otherUser && (otherUser.attributes?.roles || []).includes('group_admin');
    const allowViaOtherUser = isStationGroupsPath && otherUserIsGroupAdmin;

    if (!hasRequiredRole && !allowViaOtherUser) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
